(() => {
  if (window.PLCSiteSound || window.Shopify?.designMode) return;
  const sound = document.querySelector('[data-plc-site-sound]');
  const audio = sound?.querySelector('[data-plc-background-audio]');
  const toggle = sound?.querySelector('[data-plc-sound-toggle]');
  const status = sound?.querySelector('[data-plc-sound-status]');
  if (!sound || !audio || !toggle) return;
  let pageRoot = document.querySelector('[data-plc-route-root]');
  let context;
  let gain;
  let primed = false;
  let started = false;
  let ready = false;
  let loading = false;
  let transition = 0;
  let controller;
  let renderedUrl = location.href;
  let userMuted = false;
  const read = (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  };
  const remember = () => {
    try { localStorage.setItem('plc-site-sound-v1', userMuted ? 'off' : 'on'); } catch {}
  };
  userMuted = read('plc-site-sound-v1') === 'off';
  const sourceKey = (url) => new URL(url, location.href).href;
  const loadedScripts = new Set(Array.from(document.scripts).filter(s => s.src).map(s => sourceKey(s.src)));
  // Shopify's compiled bundles only initialize the section/snippet names on their script tag.
  const componentScripts = new Map(['sections-script', 'snippets-script'].map(id => {
    const attribute = id === 'sections-script' ? 'data-sections' : 'data-snippets';
    return [id, { attribute, loaded: new Set((document.getElementById(id)?.getAttribute(attribute) || '').split(',').filter(Boolean)) }];
  }));
  const routeStyles = new Set(document.head.querySelectorAll('style, link[rel="stylesheet"]'));
  const styleKey = (node) => node.tagName === 'LINK' ? sourceKey(node.getAttribute('href')) : node.textContent;
  const entranceActive = () => {
    const entrance = document.querySelector('#plc-entrance');
    return entrance && entrance.dataset.state !== 'complete' && !entrance.classList.contains('plc-dismissed');
  };
  const videoActive = () => Array.from(document.querySelectorAll('video, audio')).some(media => media !== audio && !media.paused && !media.ended && !media.muted && media.volume > 0) || Array.from(document.querySelectorAll('iframe')).some(frame => /(?:youtube(?:-nocookie)?\.com\/embed|player\.vimeo\.com\/video)/.test(frame.src) && frame.getClientRects().length);
  const canPlay = () => ready && !userMuted && !document.hidden && !videoActive();
  const setUi = (playing, message) => {
    sound.dataset.playing = String(playing);
    const label = playing ? sound.dataset.soundOff : sound.dataset.soundOn;
    toggle.setAttribute('aria-pressed', String(playing));
    toggle.setAttribute('aria-label', label);
    toggle.title = label;
    if (status && message) status.textContent = message;
  };
  const makeContext = () => {
    if (context) return true;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return false;
    try {
      context = new AudioContext();
      gain = context.createGain();
      gain.gain.value = 0;
      context.createMediaElementSource(audio).connect(gain);
      gain.connect(context.destination);
      return true;
    } catch {
      context = undefined;
      gain = undefined;
      return false;
    }
  };
  const fadeTo = (value) => {
    if (!gain || !context) return;
    gain.gain.cancelScheduledValues(context.currentTime);
    gain.gain.setTargetAtTime(value, context.currentTime, value ? .45 : .04);
  };
  const pause = () => {
    fadeTo(0);
    audio.pause();
    setUi(false);
  };
  const play = async (gesture = false) => {
    if (!canPlay() || loading) return;
    loading = true;
    try {
      if (gesture || context) {
        makeContext();
        if (context?.state === 'suspended') await context.resume();
      }
      if (context && context.state !== 'running') throw new Error('activation');
      if (!started) {
        try { audio.currentTime = 0; } catch {}
      }
      await audio.play();
      primed = true;
      if (!canPlay()) { pause(); return; }
      started = true;
      fadeTo(1);
      setUi(true, sound.dataset.playingLabel);
    } catch (error) {
      setUi(false, error.name === 'NotAllowedError' || error.message === 'activation' ? sound.dataset.paused : sound.dataset.error);
    } finally {
      loading = false;
    }
  };
  const prime = () => {
    if (userMuted || started || primed || !makeContext()) return;
    gain.gain.value = 0;
    context.resume().catch(() => {});
    audio.play().then(() => {
      primed = true;
      if (ready) {
        try { audio.currentTime = 0; } catch {}
        started = true;
        if (canPlay()) { fadeTo(1); setUi(true, sound.dataset.playingLabel); }
        else pause();
      }
    }).catch(() => { primed = false; });
  };
  const pageReady = () => {
    ready = !entranceActive();
    sound.hidden = !ready;
    if (!ready) return;
    if (primed && !started) {
      try { audio.currentTime = 0; } catch {}
    }
    if (canPlay()) play();
    renderedUrl = location.href;
  };
  toggle.addEventListener('click', () => {
    if (!audio.paused && !userMuted && ready) {
      userMuted = true;
      remember();
      pause();
      if (status) status.textContent = sound.dataset.paused;
    } else {
      userMuted = false;
      remember();
      play(true);
    }
  });
  document.addEventListener('click', event => {
    if (event.target.closest('#plc-enter, #plc-skip')) prime();
  }, true);
  window.addEventListener('plc:entrance-complete', pageReady);
  document.addEventListener('plc:page-ready', pageReady);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause();
    else if (ready) play();
  });
  for (const name of ['play', 'pause', 'ended', 'volumechange']) {
    document.addEventListener(name, event => {
      if (event.target === audio || !(event.target instanceof HTMLMediaElement)) return;
      if (!ready) return;
      if (videoActive()) pause();
      else play();
    }, true);
  }
  const mediaObserver = new MutationObserver(records => {
    if (!ready || !records.some(record => record.type === 'attributes' ? record.target.tagName === 'IFRAME' : Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(node => node.nodeType === 1 && (node.tagName === 'IFRAME' || node.querySelector?.('iframe'))))) return;
    if (videoActive()) pause();
    else play();
  });
  mediaObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  const isRoute = (url) => url.origin === location.origin && /^(?:\/(?:products|collections|pages|policies)(?:\/|$)|\/cart\/?$|\/$)/.test(url.pathname) && !url.searchParams.has('section_id') && !url.searchParams.has('sections') && !url.searchParams.has('plc_replay') && !/\.(?:json|js|xml|pdf|mp3|png|jpe?g|webp)$/i.test(url.pathname);
  const saveScroll = () => {
    history.replaceState({ ...history.state, plcRoute: { x: scrollX, y: scrollY } }, '', location.href);
  };
  const themeScript = (script) => {
    if (!script.src) return false;
    const url = new URL(script.src, location.href);
    return /\/cdn\/shop\/t\/\d+\/(?:assets|compiled_assets)\//.test(url.pathname) && url.origin === location.origin && !url.pathname.endsWith('/plc-site-session.js');
  };
  const prepareStyles = async (doc) => {
    const incoming = Array.from(doc.head.querySelectorAll('style, link[rel="stylesheet"]'));
    const keep = new Set();
    const waiting = incoming.map(source => {
      const key = styleKey(source);
      const existing = Array.from(routeStyles).find(node => styleKey(node) === key && node.isConnected);
      if (existing) { keep.add(existing); return Promise.resolve(); }
      const node = document.importNode(source, true);
      keep.add(node);
      if (node.tagName !== 'LINK') { document.head.appendChild(node); return Promise.resolve(); }
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('style')), 8000);
        node.onload = () => { clearTimeout(timeout); resolve(); };
        node.onerror = () => { clearTimeout(timeout); reject(new Error('style')); };
        document.head.appendChild(node);
      });
    });
    await Promise.all(waiting);
    return keep;
  };
  const loadScripts = async (scripts) => {
    for (const source of scripts) {
      if (!themeScript(source)) continue;
      const key = sourceKey(source.src);
      const components = componentScripts.get(source.id);
      const missing = components ? (source.getAttribute(components.attribute) || '').split(',').filter(name => name && !components.loaded.has(name)) : [];
      if (components ? !missing.length : loadedScripts.has(key)) continue;
      loadedScripts.add(key);
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = key;
        if (source.type) script.type = source.type;
        if (components) {
          document.getElementById(source.id)?.remove();
          script.id = source.id;
          script.setAttribute(components.attribute, missing.join(','));
        }
        script.onload = () => {
          if (components) {
            missing.forEach(name => components.loaded.add(name));
            script.setAttribute(components.attribute, Array.from(components.loaded).join(','));
          }
          resolve();
        };
        script.onerror = () => { loadedScripts.delete(key); reject(new Error('script')); };
        document.head.appendChild(script);
      });
    }
  };
  const updateHead = (doc) => {
    document.title = doc.title;
    for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]']) {
      document.head.querySelectorAll(selector).forEach(node => node.remove());
      doc.head.querySelectorAll(selector).forEach(node => document.head.appendChild(document.importNode(node, true)));
    }
    document.documentElement.lang = doc.documentElement.lang;
    document.documentElement.className = doc.documentElement.className.replace(/\bno-js\b/g, 'js');
    document.documentElement.style.overflow = '';
    document.body.className = doc.body.className;
    document.body.removeAttribute('style');
  };
  const runInlineSections = () => {
    pageRoot.querySelectorAll('[id^="shopify-section-"] script:not([src])').forEach(source => {
      if (source.type && !/^(?:text|application)\/javascript$/.test(source.type)) return;
      const script = document.createElement('script');
      script.textContent = source.textContent;
      source.replaceWith(script);
    });
  };
  const navigate = async (href, options = {}) => {
    const url = new URL(href, location.href);
    if (!pageRoot || !isRoute(url) || window.Shopify?.designMode) {
      location.assign(url.href);
      return;
    }
    const id = ++transition;
    controller?.abort();
    controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    pageRoot.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('page');
      const finalUrl = new URL(response.url);
      if (!isRoute(finalUrl)) throw new Error('redirect');
      finalUrl.hash = url.hash;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // DOMParser exposes no-JavaScript fallback styles as live nodes; this session already runs JavaScript.
      doc.querySelectorAll('noscript').forEach(node => node.remove());
      const fresh = doc.querySelector('[data-plc-route-root]');
      if (!fresh || !doc.querySelector('[data-plc-site-sound]')) throw new Error('layout');
      const nextStyles = await prepareStyles(doc);
      if (id !== transition) return;
      document.dispatchEvent(new CustomEvent('plc:page-before'));
      if (!options.history) {
        saveScroll();
        history.pushState({ plcRoute: { x: 0, y: 0 } }, '', finalUrl.href);
      }
      updateHead(doc);
      const scripts = Array.from(doc.querySelectorAll('script[src]'));
      const replacement = document.importNode(fresh, true);
      replacement.setAttribute('aria-busy', 'true');
      replacement.querySelectorAll('script[src]').forEach(node => node.remove());
      pageRoot.replaceWith(replacement);
      pageRoot = replacement;
      renderedUrl = location.href;
      routeStyles.forEach(node => { if (!nextStyles.has(node)) node.remove(); });
      routeStyles.clear();
      nextStyles.forEach(node => routeStyles.add(node));
      await loadScripts(scripts);
      if (id !== transition) return;
      runInlineSections();
      document.dispatchEvent(new CustomEvent('plc:page-ready'));
      window.Tally?.loadEmbeds();
      pageRoot.removeAttribute('aria-busy');
      renderedUrl = location.href;
      requestAnimationFrame(() => {
        if (id !== transition) return;
        if (options.history) window.scrollTo({ left: options.scroll?.x || 0, top: options.scroll?.y || 0, behavior: 'instant' });
        else if (url.hash) {
          const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
          if (target) target.scrollIntoView();
          else window.scrollTo({ top: 0, behavior: 'instant' });
        } else window.scrollTo({ top: 0, behavior: 'instant' });
        const main = document.querySelector('#MainContent');
        if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        if (!options.history) main?.focus({ preventScroll: true });
      });
    } catch (error) {
      if (id !== transition) return;
      if (options.history) location.reload();
      else location.assign(url.href);
    } finally {
      clearTimeout(timeout);
      if (id === transition) pageRoot?.removeAttribute('aria-busy');
    }
  };
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self') || link.closest('[data-plc-gallery-dialog]') || link.matches('[data-plc-gallery-open], [data-ps-archive-open], [data-plc-native-navigation]')) return;
    const url = new URL(link.href, location.href);
    if (!isRoute(url) || entranceActive() || link.getAttribute('href')?.startsWith('#')) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    event.preventDefault();
    navigate(url.href);
  });
  window.addEventListener('popstate', event => {
    if (new URL(renderedUrl).pathname === location.pathname && new URL(renderedUrl).search === location.search) return;
    if (!event.state?.plcRoute) { location.reload(); return; }
    navigate(location.href, { history: true, scroll: event.state.plcRoute });
  });
  window.addEventListener('pagehide', () => {
    try { sessionStorage.setItem('plc-site-sound-time-v1', String(audio.currentTime)); } catch {}
    pause();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) pageReady(); });
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  saveScroll();
  window.PLCSiteSound = Object.freeze({ navigate });
  pageReady();
})();
